import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaEnvelope, FaArrowLeft, FaCheck, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './Auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setEmail(data.email);
    
    try {
      // API call to request password reset
      // This would be your WordPress API endpoint
      const response = await fetch('/wp-json/xpunters/v1/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        toast.success('Password reset link sent to your email!');
      } else {
        toast.error(result.message || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Forgot password error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card success-card">
            <div className="success-icon">
              <FaCheck />
            </div>
            <h2>Check Your Email</h2>
            <p>
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="success-hint">
              Please check your inbox and follow the instructions to reset your password.
            </p>
            <div className="success-actions">
              <button 
                onClick={() => navigate('/login')} 
                className="btn btn-primary"
              >
                Back to Login
              </button>
              <button 
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }} 
                className="btn btn-secondary"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/login" className="back-to-login">
              <FaArrowLeft /> Back to Login
            </Link>
            <div className="auth-logo">
              <span className="logo-icon">🔐</span>
              <h1>Forgot Password</h1>
            </div>
            <p className="auth-subtitle">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  placeholder="Enter your registered email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: { 
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email'
                    }
                  })}
                />
              </div>
              {errors.email && (
                <span className="error-message">{errors.email.message}</span>
              )}
            </div>

            <div className="forgot-info">
              <p>We'll send a new password to your email address.</p>
              <p className="info-note">Make sure to check your spam folder if you don't see it.</p>
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? <LoadingSpinner size="small" /> : 'Send Reset Link'}
            </button>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <p className="auth-footer">
              Remember your password? <Link to="/login">Login</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;